import TheStrapLine from '@/components/header/strap-line/strap-line.component.vue';
import ContentBlock from '@/components/shared/ContentBlock.vue';
import i18n from '@/i18n';
import ConfigurationService from '@/services/configuration.service';
import UserService from '@/services/user.service';
import { hasAccess, redirectUrl, visibleMenu } from '@/shared/route-utils';
import { getLanguage, getLookAndFeel, handleSignOut } from '@/shared/utils';
import store from '@/store';
import LanguageDropdown from './language-dropdown.component.vue';
import { getStraplineBackgroundColor } from './strap-line/use-straplines-bg-color';
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { DotsHorizontalIcon, MenuIcon, TranslateIcon, UserIcon, XIcon } from '@heroicons/vue/outline';
import { computed, defineComponent, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import router from '@/router';
import { StrapLineConfig } from '../../../../common/src';

type NavigationItem = {
  name: string;
  route: string;
  cy: string;
  access: string;
  order: number;
};

export default defineComponent({
  name: 'TheHeader',
  props: {
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuIcon,
    XIcon,
    TranslateIcon,
    UserIcon,
    DotsHorizontalIcon,
    LanguageDropdown,
    ContentBlock,
    TheStrapLine,
  },
  setup() {
    const userService = inject('userService') as UserService;
    const configurationService = inject('configurationService') as ConfigurationService;
    const route = useRoute();

    const banner = ref<string | null>(null);
    const logo = ref<string | null>(null);
    const title = ref<string | null>(null);
    const more = ref(false);
    const showStrapLine = ref(false);
    const navigation = ref<NavigationItem[]>([]);
    const greedyNavigationHiddenItems = ref(new Set<number>());
    let greedyNavResizeObserver: ResizeObserver | null = null;

    const natigationItems = computed(() => {
      if (route.meta.hideNavbarMenu) {
        return [];
      }
      return navigation.value;
    });

    const hideNavbarMenuItems = computed(() => route.meta.hideNavbarMenu);

    const straplineBackgroundColor = computed(() => {
      const straplines = store.state.config?.strapLines?.htmlContents as Record<string, StrapLineConfig>;

      return getStraplineBackgroundColor(straplines, route.path, store.state.currentLocale?.locale);
    });

    const isErrorPage = computed(() => route.name === 'Error404' || route.name === 'UnauthorizedError' || route.name === 'SSOTokenError');
    const isProfileRoute = computed(() => route.name === 'Profile');

    const showSableBannerHeader = computed(() => {
      if (!banner.value || store.state.headerType === 'TINY' || route.path.startsWith('/join')) {
        return false;
      }

      // The profile page only shows its banner on the personal-info tab. Match by route name,
      // not path, so a trailing slash can't slip past the tab restriction.
      const selectedTab = Array.isArray(route.query.selectedTab) ? route.query.selectedTab[0] : route.query.selectedTab;
      return route.name !== 'Profile' || !selectedTab || selectedTab === 'my-personal-info';
    });

    const updatedUser = (): void => {
      navigation.value.length = 0;
      const menuConfig = store.state.config?.menu as Record<string, unknown> | undefined;
      router.getRoutes().forEach(currentRoute => {
        if (currentRoute.meta && currentRoute.meta.navbarName) {
          if (hasAccess(currentRoute, store.state.user) && visibleMenu(currentRoute, store.state.config)) {
            navigation.value.push({
              name: String(currentRoute.meta.navbarName),
              route: currentRoute.path.replace('/:selectedMemberKey', ''),
              cy: String(currentRoute.meta.cy),
              access: String(currentRoute.meta.access),
              order: Number(menuConfig?.[String(currentRoute.meta?.sysprop)]),
            });
          }
        }
      });

      navigation.value.sort((a, b) => a.order - b.order);
    };

    const greedyNav = (): void => {
      const padding = 12;
      const greedyNavElement = document.getElementById('greedy-nav') as HTMLElement | null;
      if (greedyNavElement) {
        const greedyNavLeft = greedyNavElement.getBoundingClientRect().left;
        const greedyNavRight = greedyNavElement.getBoundingClientRect().right;
        const greedyNavLinks = greedyNavElement.children as HTMLCollectionOf<HTMLElement>;
        let offset = greedyNavLeft;
        const hiddenItems = new Set<number>();
        for (let i = 0; i < greedyNavLinks.length; i++) {
          const navLink = greedyNavLinks[i];
          // Reset visibility before measuring so a previous narrower layout cannot affect this pass.
          navLink.style.visibility = 'visible';
          if (navLink.getBoundingClientRect().right > greedyNavRight + padding / 2) {
            navLink.style.visibility = 'hidden';
            hiddenItems.add(i);
          } else {
            offset += navLink.clientWidth + padding;
          }
        }
        greedyNavigationHiddenItems.value = hiddenItems;
        more.value = hiddenItems.size > 0;
        void nextTick(() => {
          const moreMenu = document.getElementById('more-menu') as HTMLElement | null;
          if (moreMenu) {
            moreMenu.style.left = `${offset - 40}px`;
          }
        });
      }
    };

    const updatedConfig = (): void => {
      logo.value = getLookAndFeel(store.state.config, 'siteLogo');
      showStrapLine.value = !isErrorPage.value && !!store.state.config?.strapLines?.htmlContents;
      banner.value = getLookAndFeel(store.state.config, route.meta.banner);

      greedyNav();
    };

    const updatedRoute = (): void => {
      updatedConfig();

      if (route.meta.title) {
        title.value = String(route.meta.title);
        window.document.title = i18n.global.t(String(route.meta.title));
      } else {
        title.value = window.document.title = 'Exerp';
      }
    };

    const signOut = (): void => {
      handleSignOut(userService, configurationService);
    };

    const gotoHomeUrl = (): void => {
      let redirect: string | undefined;
      if (store.state.user) {
        redirect = store.state.config?.redirects.homeMember;
      } else {
        redirect = store.state.config?.redirects.homeAnonymous;
      }
      if (redirect) {
        redirectUrl(redirect);
      }
    };

    const routerLinkMethod = (href: string): void => {
      void router.push(href);
    };

    watch(() => store.state.user, updatedUser, { immediate: true, deep: true });
    // the links are measured by the resize observer only when the nav's own width changes; a
    // rebuilt list or a late logo/font load changes the links' widths without changing the nav's
    watch(navigation, () => void nextTick(greedyNav), { deep: true });
    watch(route, updatedRoute, { immediate: true });
    watch(() => store.state.config, updatedConfig, { immediate: true, deep: true });

    const currentLanguage = getLanguage(localStorage.language);
    i18n.global.locale = currentLanguage.code;
    store.commit('currentLocale', currentLanguage);

    onMounted(() => {
      greedyNavResizeObserver = new ResizeObserver(entries => {
        window.requestAnimationFrame(() => {
          if (entries.length) {
            greedyNav();
          }
        });
      });
      const greedyNavElement = document.getElementById('greedy-nav') as HTMLElement | null;
      if (greedyNavElement) {
        greedyNavResizeObserver.observe(greedyNavElement);
      }
      window.addEventListener('load', greedyNav);
      void nextTick(() => {
        greedyNav();
        window.requestAnimationFrame(greedyNav);
      });
      void document.fonts?.ready.then(greedyNav);
    });

    onBeforeUnmount(() => {
      greedyNavResizeObserver?.disconnect();
      window.removeEventListener('load', greedyNav);
    });

    return {
      banner,
      greedyNavigationHiddenItems,
      gotoHomeUrl,
      hideNavbarMenuItems,
      isProfileRoute,
      logo,
      more,
      natigationItems,
      routerLinkMethod,
      showSableBannerHeader,
      showStrapLine,
      signOut,
      straplineBackgroundColor,
      title,
    };
  },
});
